<template>
  <div>
    <!-- NOC Classification -->
    <div class="card mb-3">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h5 class="card-title mb-0"><i class="fas fa-tag mr-2"></i>NOC Classification</h5>
        <div>
          <b-button
            variant="outline-secondary"
            size="sm"
            class="mr-1"
            @click="showNocPreview"
            :disabled="isLoadingPreview"
            title="Preview AI Input"
          >
            <b-spinner v-if="isLoadingPreview" small></b-spinner>
            <i v-else class="fas fa-eye"></i>
          </b-button>
          <b-button
            variant="outline-primary"
            size="sm"
            @click="openRerunModal"
            :title="offer.noc_number ? 'Re-run AI matching' : 'Run AI matching'"
          >
            <i class="fas fa-sync-alt"></i>
          </b-button>
        </div>
      </div>
      <div class="card-body">
        <!-- Current NOC Display -->
        <div v-if="offer.noc_number" class="mb-3">
          <div class="d-flex align-items-center mb-2">
            <strong class="mr-2">Code:</strong>
            <code>{{ offer.noc_number }}</code>
            <CopyButton :text="String(offer.noc_number)" />
            <b-badge v-if="offer.noc_manually_overridden" variant="warning" class="ml-2">Manual</b-badge>
            <b-badge v-else-if="offer.noc_ai_confidence" variant="info" class="ml-2">AI</b-badge>
          </div>

          <div v-if="offer.noc_number_relation" class="mb-2">
            <strong class="small text-muted">Class Title:</strong>
            <div class="ml-2">
              <div class="small"><span class="text-muted">EN:</span> {{ offer.noc_number_relation.name_en }}</div>
              <div class="small"><span class="text-muted">FR:</span> {{ offer.noc_number_relation.name_fr }}</div>
            </div>
          </div>

          <div class="mb-2">
            <strong class="small text-muted">Job Title:</strong>
            <div v-if="offer.noc_job_title" class="ml-2">
              <div class="small"><span class="text-muted">EN:</span> {{ offer.noc_job_title.title_en }}</div>
              <div class="small"><span class="text-muted">FR:</span> {{ offer.noc_job_title.title_fr }}</div>
            </div>
            <div v-else class="ml-2 small text-warning">
              <em>Not assigned (below confidence threshold)</em>
            </div>
          </div>

          <div v-if="offer.noc_ai_confidence" class="mt-2">
            <strong class="small text-muted">Confidence:</strong>
            <b-progress :value="offer.noc_ai_confidence * 100" :max="100" class="mt-1" height="8px">
              <b-progress-bar :value="offer.noc_ai_confidence * 100"></b-progress-bar>
            </b-progress>
            <small class="text-muted">{{ (offer.noc_ai_confidence * 100).toFixed(1) }}%</small>
          </div>
          <div v-if="offer.noc_last_matched_at" class="mt-2">
            <small class="text-muted">Matched {{ formatTimeAgo(offer.noc_last_matched_at) }}</small>
          </div>
        </div>
        <div v-else class="mb-3">
          <span class="text-muted">No NOC assigned</span>
        </div>

        <!-- Manual Override -->
        <hr />
        <div class="mt-3">
          <label class="small text-muted mb-2">Manual Override</label>
          <NocFormGroup
            v-model="selectedNocJobTitle"
            label=""
            :disabled="isSavingNoc"
          />
          <b-button
            variant="primary"
            size="sm"
            block
            :disabled="!selectedNocJobTitle || isSavingNoc"
            @click="saveNocOverride"
          >
            <b-spinner v-if="isSavingNoc" small class="mr-1"></b-spinner>
            {{ isSavingNoc ? 'Saving...' : 'Set NOC Manually' }}
          </b-button>
        </div>
      </div>
    </div>

    <!-- AI Summary -->
    <div class="card mb-3">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h5 class="card-title mb-0"><i class="fas fa-robot mr-2"></i>AI Summary</h5>
        <b-button
          variant="outline-primary"
          size="sm"
          @click="generateSummary"
          :disabled="isGeneratingSummary"
          :title="hasAiSummary ? 'Regenerate AI Summary' : 'Generate AI Summary'"
        >
          <b-spinner v-if="isGeneratingSummary" small></b-spinner>
          <i v-else class="fas fa-sync-alt"></i>
        </b-button>
      </div>
      <div class="card-body">
        <div v-if="hasAiSummary">
          <div v-for="(content, lang) in offer.contents" :key="'summary-' + lang">
            <div v-if="content.ai_summary" class="mb-3">
              <strong class="small text-muted">{{ lang.toUpperCase() }}:</strong>
              <div class="d-flex align-items-start">
                <div class="ai-summary-text mt-1 flex-grow-1">{{ content.ai_summary }}</div>
                <CopyButton :text="content.ai_summary" />
              </div>
              <small v-if="content.ai_summary_generated_at" class="text-muted">
                Generated {{ formatTimeAgo(content.ai_summary_generated_at) }}
              </small>
            </div>
          </div>
        </div>
        <div v-else class="text-muted">
          <p class="mb-2">No AI summary generated yet.</p>
          <small>Click the <i class="fas fa-sync-alt mx-1"></i> button to generate a summary of the job's role and tasks. This improves NOC matching accuracy.</small>
        </div>
      </div>
    </div>

    <!-- Rerun NOC Modal -->
    <RerunNocModal
      ref="rerunNocModal"
      :offerId="offerId"
      :hasAiSummary="hasAiSummary"
      @success="onRerunSuccess"
    />

    <!-- NOC Preview Modal -->
    <b-modal
      v-model="showPreviewModal"
      title="NOC AI Matching Preview"
      size="xl"
      ok-only
      ok-title="Close"
      scrollable
    >
      <template v-if="nocPreview">
        <div class="mb-4">
          <h6 class="text-primary mb-2"><i class="fas fa-cog mr-1"></i> AI Configuration</h6>
          <div class="bg-light p-3 rounded">
            <div class="row">
              <div class="col-md-3"><strong>Model:</strong> {{ nocPreview.config.model }}</div>
              <div class="col-md-3"><strong>Temperature:</strong> {{ nocPreview.config.temperature }}</div>
              <div class="col-md-3"><strong>Max Tokens:</strong> {{ nocPreview.config.max_tokens }}</div>
              <div class="col-md-3"><strong>Job Title Threshold:</strong> {{ nocPreview.config.job_title_confidence_threshold }}</div>
            </div>
          </div>
        </div>

        <div class="mb-4">
          <h6 class="text-primary mb-2"><i class="fas fa-file-alt mr-1"></i> Offer Context Sent to AI</h6>
          <div class="bg-light p-3 rounded">
            <div class="mb-2">
              <strong>Title:</strong> {{ nocPreview.offer_context.title }}
            </div>
            <div class="mb-2">
              <strong>Categories:</strong>
              <span v-if="nocPreview.offer_context.categories.length">
                <b-badge v-for="cat in nocPreview.offer_context.categories" :key="cat" variant="primary" class="mr-1">{{ cat }}</b-badge>
              </span>
              <span v-else class="text-muted">None</span>
            </div>
            <div class="mb-2">
              <strong>AI Summary:</strong>
              <div v-if="nocPreview.offer_context.ai_summary" class="ai-summary-text mt-1">
                {{ nocPreview.offer_context.ai_summary }}
                <div class="text-success small mt-1">
                  <i class="fas fa-check-circle mr-1"></i>
                  This summary is used for NOC matching (improves accuracy)
                </div>
              </div>
              <div v-else class="alert alert-warning small mt-1 mb-0">
                <i class="fas fa-exclamation-triangle mr-1"></i>
                No AI summary available. Generate one to improve matching accuracy.
              </div>
            </div>
            <div>
              <strong>Description ({{ nocPreview.offer_context.description_length }} chars):</strong>
              <div class="alert alert-secondary small mt-1 mb-0">
                <i class="fas fa-info-circle mr-1"></i>
                Description is <strong>NOT</strong> sent to AI by default.
                Use the <i class="fas fa-sync-alt mx-1"></i> re-run button to optionally include it.
              </div>
            </div>
          </div>
        </div>

        <div class="mb-4">
          <h6 class="text-primary mb-2"><i class="fas fa-list mr-1"></i> Candidate NOC Codes ({{ nocPreview.candidate_count }})</h6>
          <div class="candidate-nocs-container">
            <b-table
              :items="nocPreview.candidate_nocs"
              :fields="nocPreviewFields"
              small
              hover
              responsive
              sticky-header="300px"
            >
              <template #cell(noc_code)="{ item }">
                <router-link
                  :to="{ name: 'noc-detail', params: { nocCode: item.noc_code } }"
                  target="_blank"
                >
                  <code>{{ String(item.noc_code).padStart(5, '0') }}</code>
                </router-link>
              </template>
            </b-table>
          </div>
        </div>

        <div class="mb-2">
          <h6 class="text-primary mb-2"><i class="fas fa-terminal mr-1"></i> Full Prompt Sent to AI</h6>
          <pre class="bg-dark text-light p-3 rounded prompt-preview">{{ nocPreview.prompts.noc_code_prompt }}</pre>
        </div>
      </template>
      <template v-else>
        <div class="text-center py-4">
          <b-spinner></b-spinner>
          <p class="mt-2">Loading preview...</p>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { BBadge, BButton, BSpinner, BProgress, BProgressBar, BModal, BTable } from "bootstrap-vue";
import NocFormGroup from "@/components/Offers/NocFormGroup.vue";
import RerunNocModal from "@/components/Offers/RerunNocModal.vue";
import CopyButton from "@/components/CopyButton.vue";

export default {
  components: { BBadge, BButton, BSpinner, BProgress, BProgressBar, BModal, BTable, NocFormGroup, RerunNocModal, CopyButton },
  props: {
    offer: { type: Object, required: true },
    offerId: { type: [String, Number], required: true }
  },
  data() {
    return {
      isSavingNoc: false,
      selectedNocJobTitle: null,
      showPreviewModal: false,
      isLoadingPreview: false,
      isGeneratingSummary: false,
      nocPreview: null,
      nocPreviewFields: [
        { key: "noc_code", label: "Code", thStyle: { width: "80px" } },
        { key: "title_en", label: "English Title" },
        { key: "title_fr", label: "French Title" }
      ]
    };
  },
  computed: {
    hasAiSummary() {
      if (!this.offer.contents) return false;
      return Object.values(this.offer.contents).some(c => c.ai_summary);
    }
  },
  methods: {
    formatTimeAgo(dateStr) {
      if (!dateStr) return "-";
      const date = new Date(dateStr);
      const now = new Date();
      const seconds = Math.floor((now - date) / 1000);
      if (seconds < 60) return "just now";
      const minutes = Math.floor(seconds / 60);
      if (minutes < 60) return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
      const days = Math.floor(hours / 24);
      if (days < 30) return `${days} day${days > 1 ? "s" : ""} ago`;
      const months = Math.floor(days / 30);
      if (months < 12) return `${months} month${months > 1 ? "s" : ""} ago`;
      const years = Math.floor(months / 12);
      return `${years} year${years > 1 ? "s" : ""} ago`;
    },
    openRerunModal() {
      this.$refs.rerunNocModal.openModal();
    },
    onRerunSuccess() {
      setTimeout(() => this.$emit("offer-updated"), 3000);
    },
    async saveNocOverride() {
      if (!this.selectedNocJobTitle) return;
      this.isSavingNoc = true;
      try {
        await this.$http.put(`/enroll_admin/offer/${this.offerId}`, {
          noc_job_title: this.selectedNocJobTitle.id,
          categories: this.offer.offer_attributes?.filter(a => a.type === "category").map(a => a.id) || [],
          specializations: this.offer.offer_attributes?.filter(a => a.type === "specialization").map(a => a.id) || [],
          experiences: this.offer.offer_attributes?.filter(a => a.type === "experience").map(a => a.id) || []
        });
        this.$bvToast.toast("NOC updated successfully", { title: "Success", variant: "success", solid: true });
        this.$emit("offer-updated");
        this.selectedNocJobTitle = null;
      } catch {
        this.$bvToast.toast("Failed to update NOC", { title: "Error", variant: "danger", solid: true });
      } finally {
        this.isSavingNoc = false;
      }
    },
    async showNocPreview() {
      this.isLoadingPreview = true;
      this.nocPreview = null;
      this.showPreviewModal = true;
      try {
        const { data } = await this.$http.get(`/enroll_admin/offer/${this.offerId}/noc-matching-preview`);
        this.nocPreview = data;
      } catch {
        this.$bvToast.toast("Failed to load NOC preview", { title: "Error", variant: "danger", solid: true });
        this.showPreviewModal = false;
      } finally {
        this.isLoadingPreview = false;
      }
    },
    async generateSummary() {
      this.isGeneratingSummary = true;
      try {
        const { data } = await this.$http.post(`/enroll_admin/offer/${this.offerId}/generate-summary`, {
          force: this.hasAiSummary
        });
        const generated = Object.values(data.results).filter(r => r.status === "generated").length;
        const failedResults = Object.entries(data.results).filter(([, r]) => r.status === "failed");

        if (generated > 0) {
          this.$bvToast.toast(`Generated ${generated} AI summary(ies)`, { title: "Success", variant: "success", solid: true });
          this.$emit("offer-updated");
        } else if (failedResults.length > 0) {
          const [lang, result] = failedResults[0];
          this.$bvToast.toast(`[${lang.toUpperCase()}] ${result.message || "Unknown error"}`, { title: "Failed to generate AI summary", variant: "danger", solid: true });
        } else {
          this.$bvToast.toast("No summaries needed generation", { title: "Info", variant: "info", solid: true });
        }
      } catch (error) {
        this.$bvToast.toast(error.response?.data?.error || "Failed to generate AI summary", { title: "Error", variant: "danger", solid: true });
      } finally {
        this.isGeneratingSummary = false;
      }
    }
  }
};
</script>

<style scoped>
.card-title { font-size: 1rem; }
.ai-summary-text { background: #f8f9fa; padding: 10px; border-radius: 4px; font-size: 0.9rem; line-height: 1.5; white-space: pre-wrap; }
.prompt-preview { max-height: 400px; overflow-y: auto; white-space: pre-wrap; font-size: 0.8rem; }
.candidate-nocs-container { max-height: 350px; overflow-y: auto; }
</style>
